import * as joint from '@joint/core'

/**
 * C4 MobileDevice shape - smartphone outline
 */
export const C4MobileDevice = joint.dia.Element.define(
  'c4.MobileDevice',
  {
    size: { width: 160, height: 260 },
    attrs: {
      frame: {
        refWidth: '100%',
        refHeight: '100%',
        rx: 20,
        ry: 20,
        fill: '#438dd5',
        stroke: '#2e6295',
        strokeWidth: 3,
      },
      screen: {
        refX: '7.5%',
        refY: 25,
        refWidth: '85%',
        refHeight: 'calc(h - 65)',
        rx: 5,
        ry: 5,
        fill: '#5a9fd8',
        stroke: 'none',
      },
      speaker: {
        refX: '35%',
        refY: 12,
        refWidth: '30%',
        height: 5,
        rx: 2,
        ry: 2,
        fill: '#2e6295',
        stroke: 'none',
      },
      homeButton: {
        refX: '50%',
        refY: 'calc(h - 20)',
        r: 10,
        fill: 'transparent',
        stroke: '#2e6295',
        strokeWidth: 2,
      },
      nameLabel: {
        refX: '50%',
        refY: 80,
        textVerticalAnchor: 'middle',
        textAnchor: 'middle',
        fontSize: 12,
        fontWeight: 'bold',
        fill: '#ffffff',
        fontFamily: 'Open Sans, sans-serif',
        text: '',
      },
      typeLabel: {
        refX: '50%',
        refY: 100,
        textVerticalAnchor: 'middle',
        textAnchor: 'middle',
        fontSize: 10,
        fill: '#ffffff',
        fontFamily: 'Open Sans, sans-serif',
        text: '[Mobile App]',
      },
      descriptionLabel: {
        refX: '50%',
        refY: 125,
        textVerticalAnchor: 'top',
        textAnchor: 'middle',
        fontSize: 10,
        fill: '#ffffff',
        fontFamily: 'Open Sans, sans-serif',
        text: '',
        textWrap: {
          width: '80%',
          height: 80,
          ellipsis: true,
        },
      },
    },
  },
  {
    markup: [
      { tagName: 'rect', selector: 'frame' },
      { tagName: 'rect', selector: 'screen' },
      { tagName: 'rect', selector: 'speaker' },
      { tagName: 'circle', selector: 'homeButton' },
      { tagName: 'text', selector: 'nameLabel' },
      { tagName: 'text', selector: 'typeLabel' },
      { tagName: 'text', selector: 'descriptionLabel' },
    ],
  }
)

export function createMobileDevice(
  id: string,
  name: string,
  technology: string = '',
  description: string = '',
  x: number = 0,
  y: number = 0,
  landscape: boolean = false,
  style?: {
    background?: string
    stroke?: string
    color?: string
  }
): joint.dia.Element {
  const typeText = technology ? `[Mobile App: ${technology}]` : '[Mobile App]'

  const mobileDevice = new C4MobileDevice({
    id,
    position: { x, y },
    size: landscape ? { width: 260, height: 160 } : { width: 160, height: 260 },
    attrs: {
      frame: {
        ...(style?.background && { fill: style.background }),
        ...(style?.stroke && { stroke: style.stroke }),
      },
      screen: {
        ...(style?.background && { fill: adjustBrightness(style.background, 20) }),
      },
      speaker: {
        ...(style?.stroke && { fill: style.stroke }),
      },
      homeButton: {
        ...(style?.stroke && { stroke: style.stroke }),
      },
      nameLabel: {
        text: name,
        ...(style?.color && { fill: style.color }),
      },
      typeLabel: {
        text: typeText,
        ...(style?.color && { fill: style.color }),
      },
      descriptionLabel: {
        text: description,
        ...(style?.color && { fill: style.color }),
      },
    },
  })

  return mobileDevice
}

function adjustBrightness(hex: string, percent: number): string {
  const num = parseInt(hex.replace('#', ''), 16)
  const r = Math.min(255, ((num >> 16) & 0xff) + Math.round(2.55 * percent))
  const g = Math.min(255, ((num >> 8) & 0xff) + Math.round(2.55 * percent))
  const b = Math.min(255, (num & 0xff) + Math.round(2.55 * percent))
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`
}
